<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'PolicyEditor',
  components: {
    AppSelect,
  },
};

interface SelectOption {
  value: string;
  label: string;
}

type FormType = 'policy' | 'policy-line' | 'custom-policy';
</script>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onBeforeUnmount, watch } from 'vue';
import Toggle from '@/components/v2/Toggle.vue';
import { RawPolicy, RawPolicySchema, defaultFormFields } from '@/types/tower/v2/PolicyForm';
import currency from 'currency.js';
import {
  referenceService,
  carrierService,
  programService,
  policyOverrideService,
  policySourceService,
} from '@/services/api';
import { policyTypeService } from '@/services/PolicyTypeService';
import { DiffField, ProgramPolicyItem } from './objects/ProgramsLibraryPanel';
import { TYPE as CustomPolicyRectType } from './objects/custom_policy_rect/CustomPolicyRect';
import { Type as PolicyRectType } from './objects/policy_rect/PolicyRect';
import { StoreMutationType } from '@/types/StoreMutationType';
import { Commit } from '@/store';
import {
  UPDATE_TOWER_CUSTOM_POLICY,
  UPDATE_POLICY,
  UpdateTowerCustomPolicyPayload,
  ISSUE_POLICY_EDITOR_SAVE,
  UPDATE_POLICY_CHILDREN,
} from '@/store/CustomEvents';
import { SideAOptions } from '@/types/SideATypes';
import { ProgramPolicy, ProgramPolicyLine } from '@/types/Programs';
import { PolicyLine } from '@/types/PolicyLine';
import AppButton from '@/components/v2/AppButton.vue';
import { Carrier, IssuingCompany } from '@/types/Carrier';
import Combobox from '@/components/v2/Combobox.vue';
import { PolicySource } from '@/types/PolicySource';

// ------
// props
// ------
const props = defineProps<{
  policy: ProgramPolicyItem;
  policyType: string;
  policyRectId: string;
}>();

// -----
// refs
// -----

const form = ref<RawPolicy>(defaultFormFields);
const diffFields = ref<DiffField[]>([]);
const carrierOptions = ref<SelectOption[]>([]);
const issuingCompanyOptions = ref<SelectOption[]>([]);
const issuingCompanyInfo = ref<IssuingCompany[]>([]);
const syndicateOptions = ref<SelectOption[]>([]);
const coverageTypeOptions = ref<SelectOption[]>([]);
const autoSaveTimeoutId = ref<NodeJS.Timeout | undefined>();
const loading = ref(false);
const policySource = ref<PolicySource | undefined>();

// -----
// data
// -----
let carriers: Carrier[] = [];

// -----------
// lifecycles
// -----------

onBeforeMount(async () => {
  loading.value = true;
  // TODO: update functions to check store if data is already loaded
  await Promise.all([getCarrierOptions(), getSyndicateOptions(), getCoverageTypeOptions()]);
  if (props.policy.carrier && !isCustomCarrier(getCarrierId(props.policy.carrier))) {
    await getIssuingCompanyOptions(getCarrierId(props.policy.carrier));
  }
  await getPolicySource();
  await loadForm();

  addEventListener(ISSUE_POLICY_EDITOR_SAVE, () => {
    handleSave();
  });
  loading.value = false;
});

onBeforeUnmount(() => {
  if (autoSaveTimeoutId.value) {
    clearTimeout(autoSaveTimeoutId.value);
  }
  removeEventListener(ISSUE_POLICY_EDITOR_SAVE, () => {
    handleSave();
  });
});

// ---------
// watchers
// ---------

// Reload the form when the user clicks on a different policy.
watch(
  () => props.policy,
  async () => {
    if (props.policy.carrier && !isCustomCarrier(getCarrierId(props.policy.carrier))) {
      await getIssuingCompanyOptions(getCarrierId(props.policy.carrier));
    }
    await getPolicySource();
    await loadForm();
  },
);

watch(
  () => form.value.carrierId.value,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue && !isCustomCarrier(newValue)) {
      getIssuingCompanyOptions(newValue);
    } else {
      form.value.issuingCompanyId.value = '';
    }
  },
);

// ---------
// handlers
// ---------

/**
 * Save the policy based on the policy type.
 */
async function handleSave(): Promise<void> {
  if (formType.value === 'policy') {
    savePolicy();
  } else if (formType.value === 'policy-line') {
    savePolicyLine();
  } else {
    saveCustomPolicy();
  }
}

async function handlePublish(): Promise<void> {
  Commit(StoreMutationType.EDIT_PUBLISH_TOWER_MODAL_STATE, {
    show: true,
    policy: await createProgramPolicyItem(),
    policyType: formType.value,
  });
}

function handleFocus(focus: boolean): void {
  if (focus) {
    Commit(StoreMutationType.SET_IS_EDITING, true);
  } else {
    Commit(StoreMutationType.SET_IS_EDITING, false);
  }
}

function handleChange(): void {
  debounce(() => {
    handleSave();
  });
}

function handlePrimaryExcessFieldChange(flag: string): void {
  var p_e_flag = 'Excess';
  if (flag === 'P01') {
    p_e_flag = 'Primary';
    form.value.attachmentPointAmount.value = '';
  }

  dispatchPolicySaveToTower<{ policyId: number; p_e_flag?: string }>(UPDATE_POLICY_CHILDREN, {
    policyId: props.policy.id,
    p_e_flag,
  });

  handleChange();
}

// ----------
// functions
// ----------

/**
 * Save the custom policy to the canvas tower object.
 */
function saveCustomPolicy(): void {
  if (!validateForm()) {
    console.log('invalid form');
    return;
  }

  dispatchPolicySaveToTower<UpdateTowerCustomPolicyPayload>(UPDATE_TOWER_CUSTOM_POLICY, {
    policyRectId: props.policyRectId ?? '',
    policy: toPolicy(form.value),
  });
}

async function savePolicy(): Promise<void> {
  if (!validateForm()) {
    console.log('invalid form');
    return;
  }

  dispatchPolicySaveToTower<{ policy: ProgramPolicyItem }>(UPDATE_POLICY, { policy: await createProgramPolicyItem() });
}

async function createProgramPolicyItem(): Promise<ProgramPolicyItem> {
  if (!formType.value) return {} as ProgramPolicyItem;

  const policy = toPolicy(form.value);
  policy.diffFields = await calculateDiffFields(policy, formType.value);
  policy.touchedFields = policy.diffFields.map((f) => {
    return f.fieldName;
  });
  if (policy.diffFields) {
    diffFields.value = policy.diffFields;
  }

  return policy;
}

async function savePolicyLine(): Promise<void> {
  if (!validateForm()) {
    console.log('invalid form');
    return;
  }

  dispatchPolicySaveToTower<{ policy: ProgramPolicyItem }>(UPDATE_POLICY, { policy: await createProgramPolicyItem() });
}

function getCarrierId(name: string): string {
  const carrier = carriers.find((c) => c.name.trim() === name.trim());
  return carrier?.ID?.toString() ?? '';
}

function getCarrierName(id: string): string {
  const carrier = carriers.find((c) => c.ID.toString() === id);
  return carrier?.name ?? '';
}

function getCarrierShortName(id: string): string {
  const carrier = carriers.find((c) => c.ID.toString() === id);
  return carrier?.shortName ?? '';
}

function getIssuingCompanyId(name: string): string {
  const issuingCompanyOption = issuingCompanyOptions.value.find((c) => c.label.trim() === name.trim());
  return issuingCompanyOption?.value ?? '';
}

function getIssuingCompanyName(id: string): string {
  const issuingCompanyOption = issuingCompanyOptions.value.find((c) => c.value === id);
  return issuingCompanyOption?.label ?? '';
}

function getIssuingCompanyRating(id: string): string {
  const issuingCompany = issuingCompanyInfo.value.find((c) => c.issuingCompanyId.toString() === id);
  return issuingCompany?.currentRating ?? '';
}

function getCoverageTypeId(coverageType: string): string {
  const coverageTypeOption = coverageTypeOptions.value.find((c) => c.label.trim() === coverageType.trim());
  return coverageTypeOption?.value ?? '';
}

function getCoverageType(coverageTypeId: string): string {
  const coverageTypeOption = coverageTypeOptions.value.find((c) => c.value === coverageTypeId);
  return coverageTypeOption?.label ?? '';
}

function getSyndicateName(id: string): string {
  const syndicateOption = syndicateOptions.value.find((s) => s.value === id);
  return syndicateOption?.label ?? '';
}

function dispatchPolicySaveToTower<T>(event: string, payload: T): void {
  dispatchEvent(new CustomEvent<T>(event, { detail: payload }));
}

async function loadForm(): Promise<void> {
  if (!props.policy) return;

  diffFields.value = props.policy.diffFields ?? [];

  // set the values and defaults
  const carrierId = getCarrierId(props.policy.carrier || '');
  form.value.carrierId = {
    value: carrierId === '' ? (props.policy.carrier ?? '') : carrierId,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.issuingCompanyId = {
    value: getIssuingCompanyId(props.policy.issuingCompany || ''),
    history: [],
    valid: true,
    visible: true,
  };

  form.value.policyPremiumAmount = {
    value: props.policy.policyPremium?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.peFlag = {
    value: props.policy.p_e_flag,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.attachmentPointAmount = {
    value: props.policy.attachmentPointValue?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.attachmentApplicable = {
    value: !props.policy.noAttachment,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.aggregateLimitAmount = {
    value: props.policy.aggregateLimit?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.aggregateLimitApplicable = {
    value: !props.policy.noLimit,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.occurrenceLimitAmount = {
    value: props.policy.occurrenceLimit?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.occurrenceLimitApplicable = {
    value: !props.policy.noOccurrenceLimit,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.quotaShareTotalAmount = {
    value: props.policy.quotaShareTotal?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.retentionAmount = {
    value: props.policy.retention?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.retentionApplicable = {
    value: !props.policy.noRetention,
    history: [],
    valid: true,
    visible: true,
  };

  form.value.customRetentionLabel = {
    value: props.policy.customRetentionLabel?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.customCarrierName = {
    value: props.policy.customCarrierName?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.leadSyndicateId = {
    value: props.policy.syndicateId?.toString() || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.coverageTypeId = {
    value: getCoverageTypeId(props.policy.coverageType || ''),
    history: [],
    valid: true,
    visible: true,
  };

  form.value.sideAValue = {
    value: props.policy.sideA || '',
    history: [],
    valid: true,
    visible: true,
  };

  form.value.brokerName = {
    value: props.policy.broker || '',
    history: [],
    valid: true,
    visible: true,
  };

  // boolean in override table, string in original table ('Runoff' or '')
  form.value.runoffFlag = {
    value: props.policy.runoff === 'Runoff',
    history: [],
    valid: true,
    visible: true,
  };

  // determine which fields are visible
  if (formType.value === 'policy') {
    form.value.carrierId.visible = policySource.value?.AllowCarrierUpdate ?? false;
    form.value.issuingCompanyId.visible = false;
    form.value.policyPremiumAmount.visible = policySource.value?.AllowPremiumUpdate ?? false;
    form.value.peFlag.visible = true;
    form.value.attachmentPointAmount.visible = true;
    form.value.aggregateLimitAmount.visible = true;
    form.value.occurrenceLimitAmount.visible = true;
    form.value.quotaShareTotalAmount.visible = true;
    form.value.retentionAmount.visible = true;
    form.value.customRetentionLabel.visible = form.value.peFlag.value === 'Primary';
    form.value.customCarrierName.visible = true;
    form.value.leadSyndicateId.visible = true;
    form.value.coverageTypeId.visible = false;
    form.value.sideAValue.visible = true;
    form.value.brokerName.visible = true;
    form.value.runoffFlag.visible = true;
  } else if (formType.value === 'policy-line') {
    form.value.carrierId.visible = false;
    form.value.issuingCompanyId.visible = false;
    form.value.policyPremiumAmount.visible = true;
    form.value.peFlag.visible = false;
    form.value.attachmentPointAmount.visible = true;
    form.value.aggregateLimitAmount.visible = true;
    form.value.occurrenceLimitAmount.visible = true;
    form.value.quotaShareTotalAmount.visible = true;
    form.value.retentionAmount.visible = true;
    form.value.customRetentionLabel.visible = form.value.peFlag.value === 'Primary';
    form.value.customCarrierName.visible = true;
    form.value.leadSyndicateId.visible = false;
    form.value.coverageTypeId.visible = false;
    form.value.sideAValue.visible = false;
    form.value.brokerName.visible = false;
    form.value.runoffFlag.visible = false;
  } else {
    form.value.carrierId.visible = true;
    form.value.issuingCompanyId.visible = true;
    form.value.policyPremiumAmount.visible = true;
    form.value.peFlag.visible = true;
    form.value.attachmentPointAmount.visible = true;
    form.value.attachmentApplicable.visible = false;
    form.value.aggregateLimitAmount.visible = true;
    form.value.aggregateLimitApplicable.visible = false;
    form.value.occurrenceLimitAmount.visible = true;
    form.value.occurrenceLimitApplicable.visible = false;
    form.value.quotaShareTotalAmount.visible = true;
    form.value.retentionAmount.visible = true;
    form.value.retentionApplicable.visible = false;
    form.value.customRetentionLabel.visible = form.value.peFlag.value === 'Primary';
    form.value.customCarrierName.visible = true;
    form.value.leadSyndicateId.visible = true;
    form.value.coverageTypeId.visible = true;
    form.value.sideAValue.visible = true;
    form.value.brokerName.visible = true;
    form.value.runoffFlag.visible = true;
  }

  if (formType.value === 'policy') {
    // add stuff needed for the bound policy flow
    // fetch the current policy override
    const currentOverride = await policyOverrideService.GetCurrentOverride(props.policy.id.toString());
    if (currentOverride) {
      const policyOverride = currentOverride.policyOverride;
      form.value.attachmentApplicable.value =
        form.value.attachmentApplicable.value !== !policyOverride.noAttachment
          ? form.value.attachmentApplicable.value
          : !policyOverride.noAttachment;
      form.value.aggregateLimitApplicable.value =
        form.value.aggregateLimitApplicable.value !== !policyOverride.noLimit
          ? form.value.aggregateLimitApplicable.value
          : !policyOverride.noLimit;
      form.value.occurrenceLimitApplicable.value =
        form.value.occurrenceLimitApplicable.value !== !policyOverride.noOccurrenceLimit
          ? form.value.occurrenceLimitApplicable.value
          : !policyOverride.noOccurrenceLimit;
      form.value.retentionApplicable.value =
        form.value.retentionApplicable.value !== !policyOverride.noRetention
          ? form.value.retentionApplicable.value
          : !policyOverride.noRetention;
    }
  } else if (formType.value === 'policy-line') {
    const currentLineOverride = await policyOverrideService.GetCurrentLineOverride(
      props.policy.id.toString(),
      props.policy.policyLineId?.toString() ?? '',
    );
    if (currentLineOverride) {
      const lineOverride = currentLineOverride.policyOverride;
      form.value.attachmentApplicable.value =
        form.value.attachmentApplicable.value !== !lineOverride.noAttachment
          ? form.value.attachmentApplicable.value
          : !lineOverride.noAttachment;
      form.value.aggregateLimitApplicable.value =
        form.value.aggregateLimitApplicable.value !== !lineOverride.noLimit
          ? form.value.aggregateLimitApplicable.value
          : !lineOverride.noLimit;
      form.value.occurrenceLimitApplicable.value =
        form.value.occurrenceLimitApplicable.value !== !lineOverride.noOccurrenceLimit
          ? form.value.occurrenceLimitApplicable.value
          : !lineOverride.noOccurrenceLimit;
      form.value.retentionApplicable.value =
        form.value.retentionApplicable.value !== !lineOverride.noRetention
          ? form.value.retentionApplicable.value
          : !lineOverride.noRetention;
    }
  }
}

function validateForm(): boolean {
  const valid = RawPolicySchema.safeParse(form.value);
  if (!valid.success) {
    // reset all valid form fields to true
    for (const key in form.value) {
      form.value[key as keyof RawPolicy].valid = true;
    }

    // set valid form fields to false
    for (const error of valid.error.issues) {
      form.value[error.path[0].toString() as keyof RawPolicy].valid = false;
    }
  }
  return valid.success;
}

/**
 *  Converts the form to a policy object that will be saved to the tower object.
 * @param form
 */
function toPolicy(form: RawPolicy): ProgramPolicyItem {
  const policy: ProgramPolicyItem = { ...props.policy };
  const carrierName = getCarrierName(form.carrierId.value); // freeform values are not an id, but what the user typed
  const carrierShortName = getCarrierShortName(form.carrierId.value);

  policy.carrier = carrierName === '' ? form.carrierId.value : carrierName;
  policy.carrierShortName = carrierShortName;
  policy.issuingCompany = getIssuingCompanyName(form.issuingCompanyId.value);
  policy.carrierRating = getIssuingCompanyRating(form.issuingCompanyId.value);
  policy.policyPremium = form.policyPremiumAmount.value ? Number(form.policyPremiumAmount.value) : undefined;
  policy.p_e_flag = form.peFlag.value;
  policy.attachmentPointValue = form.attachmentPointAmount.value ? Number(form.attachmentPointAmount.value) : undefined;
  policy.noAttachment = !form.attachmentApplicable.value;
  policy.aggregateLimit = form.aggregateLimitAmount.value ? Number(form.aggregateLimitAmount.value) : undefined;
  policy.noLimit = !form.aggregateLimitApplicable.value;
  policy.occurrenceLimit = form.occurrenceLimitAmount.value ? Number(form.occurrenceLimitAmount.value) : undefined;
  policy.noOccurrenceLimit = !form.occurrenceLimitApplicable.value;
  policy.quotaShareTotal = form.quotaShareTotalAmount.value ? form.quotaShareTotalAmount.value : undefined;
  policy.retention = form.retentionAmount.value ? Number(form.retentionAmount.value) : undefined;
  policy.noRetention = !form.retentionApplicable.value;
  policy.customRetentionLabel = form.customRetentionLabel.value;
  policy.customCarrierName = form.customCarrierName.value;
  policy.syndicateId = form.leadSyndicateId.value ? Number(form.leadSyndicateId.value) : undefined;
  policy.coverageType = getCoverageType(form.coverageTypeId.value);
  policy.sideA = form.sideAValue.value;
  policy.broker = form.brokerName.value;
  policy.runoff = form.runoffFlag.value ? 'Runoff' : '';

  return policy;
}

function normalizePolicy(policy: ProgramPolicy): ProgramPolicy {
  const normalized = { ...policy } as ProgramPolicy;
  normalized.carrier = normalizeField<string | undefined>(policy.carrier);
  normalized.policyPremium = normalizeField<number | undefined>(policy.policyPremium);
  normalized.attachmentPointValue = normalizeField<number | undefined>(policy.attachmentPointValue);
  normalized.aggregateLimit = normalizeField<number | undefined>(policy.aggregateLimit);
  normalized.occurrenceLimit = normalizeField<number | undefined>(policy.occurrenceLimit);

  // currently the quotaShareTotal is a string but really needs to be a number.
  // this is why it comes back from the backend as an emptystring instead of undefined.
  // until the backend is fixed, we need to normalize it here.
  normalized.quotaShareTotal =
    normalized.quotaShareTotal === '' ? undefined : normalizeField<string | undefined>(policy.quotaShareTotal);
  normalized.retention = normalizeField<number | undefined>(policy.retention);
  normalized.syndicateId = normalizeField<number | undefined>(policy.syndicateId);
  normalized.coverageType = normalizeField<string | undefined>(policy.coverageType);
  normalized.broker = normalizeField<string | undefined>(policy.broker);

  return normalized;
}

function normalizeField<T>(value: T): T | undefined {
  if (value === undefined || value === null) {
    return undefined;
  } else {
    return value;
  }
}

function toPolicyLine(policyLine: PolicyLine): ProgramPolicyLine {
  return {
    id: policyLine.id,
    policyId: policyLine.policyId ?? undefined,
    policyPremium: policyLine.premiumValue ?? undefined,
    attachmentPointValue: policyLine.attachmentPointValue ?? undefined,
    aggregateLimit: policyLine.aggregateLimit ?? undefined,
    occurrenceLimit: policyLine.occurrenceLimit ?? undefined,
    quotaShareTotal: policyLine.quotaShareTotal ?? undefined,
    retention: policyLine.retention ?? undefined,
  };
}

/**
 * Normalizes string values for comparison to avoid false diffs due to:
 * - Whitespace differences (leading/trailing spaces)
 * - null vs undefined vs empty string
 * Returns undefined for "no value", trimmed string otherwise.
 */
function normalizeStringForComparison(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Compares two string values, treating whitespace differences and null/undefined/empty as equivalent.
 * Returns true if the values are effectively equal.
 */
function stringsAreEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeStringForComparison(a) === normalizeStringForComparison(b);
}

/**
 * Normalizes string amount values for comparison.
 * Treats null, undefined, empty string, and '0' as "no value".
 * This is for fields like quotaShareTotal where 0 effectively means "not set".
 */
function normalizeAmountStringForComparison(value: string | null | undefined): string | null | undefined {
  if (value === null || value === undefined) {
    return value;
  }
  const trimmed = value.trim();
  // Treat empty string and '0' as "no value"
  return (trimmed === '' || trimmed === '0') ? undefined : trimmed;
}

/**
 * Compares two string amount values, treating '0' and empty as equivalent.
 */
function amountStringsAreEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeAmountStringForComparison(a) === normalizeAmountStringForComparison(b);
}

/**
 * Normalizes numeric values for comparison.
 * Treats null, undefined, 0, and NaN as "no value" (returns undefined).
 * This prevents false diffs when one side has 0 and the other has undefined.
 */
function normalizeNumberForComparison(value: number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === 0 || Number.isNaN(value)) {
    return undefined;
  }
  return value;
}

/**
 * Compares two numeric values, treating 0/null/undefined as equivalent "no value".
 * Returns true if the values are effectively equal.
 */
function numbersAreEqual(a: number | null | undefined, b: number | null | undefined): boolean {
  return normalizeNumberForComparison(a) === normalizeNumberForComparison(b);
}

async function calculateDiffFields(policy: ProgramPolicyItem, formType: FormType): Promise<DiffField[]> {
  try {
    if (formType === 'policy') {
      const currentPolicy = normalizePolicy(await programService.getProgramPolicy(policy.programId, policy.id));
      const diffFields: DiffField[] = [];
      // Use stringsAreEqual for string fields to handle whitespace/null differences
      if (!stringsAreEqual(currentPolicy.carrier, policy.carrier) && form.value.carrierId.visible) {
        diffFields.push({
          fieldName: 'carrier',
          fieldLabel: 'Carrier',
          currentValue: currentPolicy.carrier ?? '',
          newValue: policy.carrier ?? '',
        });
      }
      // Use numbersAreEqual for numeric fields to treat 0/null/undefined as equivalent
      if (!numbersAreEqual(currentPolicy.policyPremium, policy.policyPremium) && form.value.policyPremiumAmount.visible) {
        diffFields.push({
          fieldName: 'policyPremium',
          fieldLabel: 'Policy Premium',
          currentValue: toFormattedCurrency(currentPolicy.policyPremium?.toString() ?? ''),
          newValue: policyPremiumAmount.value,
        });
      }
      if (!stringsAreEqual(currentPolicy.p_e_flag, policy.p_e_flag) && form.value.peFlag.visible) {
        diffFields.push({
          fieldName: 'peFlag',
          fieldLabel: 'Layer',
          currentValue: currentPolicy.p_e_flag ?? '',
          newValue: form.value.peFlag.value,
        });
      }
      if (!numbersAreEqual(currentPolicy.attachmentPointValue, policy.attachmentPointValue) && form.value.attachmentPointAmount.visible) {
        diffFields.push({
          fieldName: 'attachmentPointValue',
          fieldLabel: 'Attachment Point',
          currentValue: toFormattedCurrency(currentPolicy.attachmentPointValue?.toString() ?? ''),
          newValue: attachmentPointAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicy.aggregateLimit, policy.aggregateLimit) && form.value.aggregateLimitAmount.visible) {
        diffFields.push({
          fieldName: 'aggregateLimit',
          fieldLabel: 'Aggregate Limit',
          currentValue: toFormattedCurrency(currentPolicy.aggregateLimit?.toString() ?? ''),
          newValue: aggregateLimitAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicy.occurrenceLimit, policy.occurrenceLimit) && form.value.occurrenceLimitAmount.visible) {
        diffFields.push({
          fieldName: 'occurrenceLimit',
          fieldLabel: 'Occurrence Limit',
          currentValue: toFormattedCurrency(currentPolicy.occurrenceLimit?.toString() ?? ''),
          newValue: occurrenceLimitAmount.value,
        });
      }
      if (!amountStringsAreEqual(currentPolicy.quotaShareTotal, policy.quotaShareTotal) && form.value.quotaShareTotalAmount.visible) {
        diffFields.push({
          fieldName: 'quotaShareTotal',
          fieldLabel: 'Total Layer Limit',
          currentValue: toFormattedCurrency(currentPolicy.quotaShareTotal ?? ''),
          newValue: quotaShareTotalAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicy.retention, policy.retention) && form.value.retentionAmount.visible) {
        diffFields.push({
          fieldName: 'retention',
          fieldLabel: 'Retention',
          currentValue: toFormattedCurrency(currentPolicy.retention?.toString() ?? ''),
          newValue: retentionAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicy.syndicateId, policy.syndicateId) && form.value.leadSyndicateId.visible) {
        diffFields.push({
          fieldName: 'syndicateId',
          fieldLabel: 'Lead Syndicate',
          currentValue: getSyndicateName(currentPolicy.syndicateId?.toString() ?? ''),
          newValue: getSyndicateName(policy.syndicateId?.toString() ?? ''),
        });
      }
      if (!stringsAreEqual(currentPolicy.coverageType, policy.coverageType) && form.value.coverageTypeId.visible) {
        diffFields.push({
          fieldName: 'coverageType',
          fieldLabel: 'Coverage Type',
          currentValue: currentPolicy.coverageType ?? '',
          newValue: policy.coverageType ?? '',
        });
      }
      if (!stringsAreEqual(currentPolicy.sideA, policy.sideA) && form.value.sideAValue.visible) {
        diffFields.push({
          fieldName: 'sideA',
          fieldLabel: 'Side A',
          currentValue: currentPolicy.sideA ?? '',
          newValue: policy.sideA,
        });
      }
      if (!stringsAreEqual(currentPolicy.runoff, policy.runoff) && form.value.runoffFlag.visible) {
        diffFields.push({
          fieldName: 'runnoff',
          fieldLabel: 'Runoff',
          currentValue: currentPolicy.runoff,
          newValue: policy.runoff,
        });
      }

      return diffFields;
    } else if (formType === 'policy-line') {
      const currentPolicyLine = toPolicyLine(
        await programService.getProgramPolicyLine(policy.programId, policy.policyLineId!),
      );
      const diffFields: DiffField[] = [];

      // Use numbersAreEqual for numeric fields to treat 0/null/undefined as equivalent
      if (!numbersAreEqual(currentPolicyLine.policyPremium, policy.policyPremium) && form.value.policyPremiumAmount.visible) {
        diffFields.push({
          fieldName: 'policyPremium',
          fieldLabel: 'Policy Premium',
          currentValue: toFormattedCurrency(currentPolicyLine.policyPremium?.toString() ?? ''),
          newValue: policyPremiumAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicyLine.attachmentPointValue, policy.attachmentPointValue) && form.value.attachmentPointAmount.visible) {
        diffFields.push({
          fieldName: 'attachmentPointValue',
          fieldLabel: 'Attachment Point',
          currentValue: toFormattedCurrency(currentPolicyLine.attachmentPointValue?.toString() ?? ''),
          newValue: attachmentPointAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicyLine.aggregateLimit, policy.aggregateLimit) && form.value.aggregateLimitAmount.visible) {
        diffFields.push({
          fieldName: 'aggregateLimit',
          fieldLabel: 'Aggregate Limit',
          currentValue: toFormattedCurrency(currentPolicyLine.aggregateLimit?.toString() ?? ''),
          newValue: aggregateLimitAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicyLine.occurrenceLimit, policy.occurrenceLimit) && form.value.occurrenceLimitAmount.visible) {
        diffFields.push({
          fieldName: 'occurrenceLimit',
          fieldLabel: 'Occurrence Limit',
          currentValue: toFormattedCurrency(currentPolicyLine.occurrenceLimit?.toString() ?? ''),
          newValue: occurrenceLimitAmount.value,
        });
      }
      if (!amountStringsAreEqual(currentPolicyLine.quotaShareTotal, policy.quotaShareTotal) && form.value.quotaShareTotalAmount.visible) {
        diffFields.push({
          fieldName: 'quotaShareTotal',
          fieldLabel: 'Total Layer Limit',
          currentValue: toFormattedCurrency(currentPolicyLine.quotaShareTotal ?? ''),
          newValue: quotaShareTotalAmount.value,
        });
      }
      if (!numbersAreEqual(currentPolicyLine.retention, policy.retention) && form.value.retentionAmount.visible) {
        diffFields.push({
          fieldName: 'retention',
          fieldLabel: 'Retention',
          currentValue: toFormattedCurrency(currentPolicyLine.retention?.toString() ?? ''),
          newValue: retentionAmount.value,
        });
      }

      return diffFields;
    }
  } catch (e) {
    console.error(e);
  }
  return [];
}

async function getCarrierOptions(): Promise<void> {
  carriers = await carrierService.list();
  carrierOptions.value = carriers.map((carrier) => {
    return {
      value: carrier.ID.toString(),
      label: carrier.shortName ?? carrier.name,
    };
  });
}

function isCustomCarrier(carrierId: string): boolean {
  return carrierOptions.value.find((c) => c.value === carrierId) === undefined;
}

async function getIssuingCompanyOptions(carrierId: string): Promise<void> {
  try {
    const issuingCompanies = await carrierService.issuingCompanies(carrierId);
    const options = issuingCompanies.map((issuingCompany) => {
      return {
        value: issuingCompany.issuingCompanyId.toString(),
        label: issuingCompany.issuingCompanyName,
      };
    });

    issuingCompanyOptions.value = options;
    issuingCompanyInfo.value = issuingCompanies;
  } catch (e) {
    issuingCompanyOptions.value = [];
    issuingCompanyInfo.value = [];
  }
}

async function getSyndicateOptions(): Promise<void> {
  const syndicates = await referenceService.syndicates();
  const options = syndicates.map((syndicate) => {
    return {
      value: syndicate.id.toString(),
      label: `${syndicate.number}-${syndicate.name!}`,
    };
  });

  syndicateOptions.value = options;
}

async function getCoverageTypeOptions(): Promise<void> {
  const coverageTypes = await policyTypeService.GetDistinctCoverageTypes();
  const options = coverageTypes.map((coverageType) => {
    return {
      value: coverageType.coverageTypeId.toString(),
      label: coverageType.coverageType,
    };
  });

  coverageTypeOptions.value = options.sort((a, b) => a.label.localeCompare(b.label));
}

async function getPolicySource(): Promise<void> {
  if (props.policy.policySourceId) {
    policySource.value = await policySourceService.getPolicySourceById(props.policy.policySourceId);
  }
}

function toFormattedCurrency(amount: string): string {
  if (amount === '') {
    return '';
  }
  return currency(amount, { fromCents: false, precision: 0, pattern: '#' }).format();
}

function toCurrency(amount: string): string {
  if (amount === '') {
    return '';
  }
  return currency(amount, { fromCents: false }).value.toString();
}

function removeCommas(amount: string): string {
  return amount.replace(/,/g, '');
}

function debounce(func: () => void): void {
  if (autoSaveTimeoutId.value) {
    clearTimeout(autoSaveTimeoutId.value);
  }

  autoSaveTimeoutId.value = setTimeout(() => {
    func();
  }, 300);
}

// ---------
// computed
// ---------

const formType = computed<FormType | undefined>(() => {
  if (props.policyType === PolicyRectType) {
    return props.policy.isChildPolicy ? 'policy-line' : 'policy';
  } else if (props.policyType === CustomPolicyRectType) {
    return 'custom-policy';
  }

  console.error('invalid policy type');
  return undefined;
});

const attachmentPointAmount = computed({
  get: () => {
    const amount = form.value.attachmentPointAmount.value;
    try {
      RawPolicySchema.shape.attachmentPointAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.attachmentPointAmount.shape.value.parse(removeCommas(amount));
      form.value.attachmentPointAmount.value = toCurrency(amount);
      form.value.attachmentPointAmount.valid = true;
    } catch (e) {
      form.value.attachmentPointAmount.value = amount;
      form.value.attachmentPointAmount.valid = false;
    }

    debounce(() => {
      form.value.attachmentPointAmount.history.push(form.value.attachmentPointAmount.value);
      handleSave();
    });
  },
});

const aggregateLimitAmount = computed({
  get: () => {
    const amount = form.value.aggregateLimitAmount.value;
    try {
      RawPolicySchema.shape.aggregateLimitAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.aggregateLimitAmount.shape.value.parse(removeCommas(amount));
      form.value.aggregateLimitAmount.value = toCurrency(amount);
      form.value.aggregateLimitAmount.valid = true;
    } catch (e) {
      form.value.aggregateLimitAmount.value = amount;
      form.value.aggregateLimitAmount.valid = false;
    }

    debounce(() => {
      form.value.aggregateLimitAmount.history.push(form.value.aggregateLimitAmount.value);
      handleSave();
    });
  },
});

const occurrenceLimitAmount = computed({
  get: () => {
    const amount = form.value.occurrenceLimitAmount.value;
    try {
      RawPolicySchema.shape.occurrenceLimitAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.occurrenceLimitAmount.shape.value.parse(removeCommas(amount));
      form.value.occurrenceLimitAmount.value = toCurrency(amount);
      form.value.occurrenceLimitAmount.valid = true;
    } catch (e) {
      form.value.occurrenceLimitAmount.value = amount;
      form.value.occurrenceLimitAmount.valid = false;
    }

    debounce(() => {
      form.value.occurrenceLimitAmount.history.push(form.value.occurrenceLimitAmount.value);
      handleSave();
    });
  },
});

const retentionAmount = computed({
  get: () => {
    const amount = form.value.retentionAmount.value;
    try {
      RawPolicySchema.shape.retentionAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.retentionAmount.shape.value.parse(removeCommas(amount));
      form.value.retentionAmount.value = toCurrency(amount);
      form.value.retentionAmount.valid = true;
    } catch (e) {
      form.value.retentionAmount.value = amount;
      form.value.retentionAmount.valid = false;
    }

    debounce(() => {
      form.value.retentionAmount.history.push(form.value.retentionAmount.value);
      handleSave();
    });
  },
});

const quotaShareTotalAmount = computed({
  get: () => {
    const amount = form.value.quotaShareTotalAmount.value;
    try {
      RawPolicySchema.shape.quotaShareTotalAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.policyPremiumAmount.shape.value.parse(removeCommas(amount));
      form.value.quotaShareTotalAmount.value = toCurrency(amount);
      form.value.quotaShareTotalAmount.valid = true;
    } catch (e) {
      form.value.quotaShareTotalAmount.value = amount;
      form.value.quotaShareTotalAmount.valid = false;
    }

    debounce(() => {
      form.value.quotaShareTotalAmount.history.push(form.value.quotaShareTotalAmount.value);
      handleSave();
    });
  },
});

const policyPremiumAmount = computed({
  get: () => {
    const amount = form.value.policyPremiumAmount.value;
    try {
      RawPolicySchema.shape.policyPremiumAmount.shape.value.parse(removeCommas(amount));
      return toFormattedCurrency(amount);
    } catch (e) {
      return amount;
    }
  },
  set: (amount) => {
    try {
      RawPolicySchema.shape.policyPremiumAmount.shape.value.parse(removeCommas(amount));
      form.value.policyPremiumAmount.value = toCurrency(amount);
      form.value.policyPremiumAmount.valid = true;
    } catch (e) {
      form.value.policyPremiumAmount.value = amount;
      form.value.policyPremiumAmount.valid = false;
    }

    debounce(() => {
      form.value.policyPremiumAmount.history.push(form.value.policyPremiumAmount.value);
      handleSave();
    });
  },
});

const showPublishButton = computed(() => {
  return formType.value !== 'custom-policy';
});

const isCustomPolicy = computed(() => {
  return formType.value === 'custom-policy';
});

const isPublishButtonEnabled = computed(() => {
  return diffFields.value.length > 0;
});

const formCarrier = computed({
  get: () => {
    return form.value.carrierId.value ?? '';
  },
  set: (value: string) => {
    form.value.carrierId.value = value;
    form.value.carrierName.value = getCarrierName(value);
  },
});

const isWorkComp = computed(() => {
  return props.policy.coverageType === 'Work Comp';
});
</script>

<template>
  <div v-if="!loading" class="tw:flex tw:flex-col tw:gap-2 tw:p-3 tw:text-xs">
    <div class="tw:flex tw:flex-wrap">
      <!-- Coverage Type -->
      <div v-if="form.coverageTypeId.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Coverage Type</span>
        <AppSelect
          v-model="form.coverageTypeId.value"
          :options="coverageTypeOptions"
          class="tw:h-9 tw:w-full"
          placeholder="Select coverage type"
          @update:modelValue="handleChange" />
      </div>
      <!-- Primary/Excess Flag -->
      <div v-if="form.peFlag.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Primary/Excess Flag</span>
        <AppSelect
          v-model="form.peFlag.value"
          :options="[
            { value: 'Primary', label: 'Primary' },
            { value: 'Excess', label: 'Excess' },
            { value: 'Fees', label: 'Fees' },
          ]"
          class="tw:h-9 tw:w-full"
          placeholder="Select type"
          @update:modelValue="handlePrimaryExcessFieldChange" />
      </div>

      <!-- Issuing Company -->
      <div v-if="form.issuingCompanyId.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Issuing Company</span>
        <AppSelect
          v-model="form.issuingCompanyId.value"
          :options="issuingCompanyOptions"
          class="tw:h-9 tw:w-full"
          placeholder="Select issuing company"
          :disabled="!form.carrierId.value || isCustomCarrier(form.carrierId.value)"
          @update:modelValue="handleChange" />
      </div>
      <!-- Aggregate Limit Amount -->
      <div v-if="form.aggregateLimitAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <div class="tw:flex tw:justify-between">
          <span class="tw:join-item tw:pl-1">Aggregate Limit</span>
          <input
            v-if="form.aggregateLimitApplicable.visible"
            type="checkbox"
            v-model="form.aggregateLimitApplicable.value"
            class="tw:toggle tw:toggle-primary tw:toggle-xs"
            @change="
              () => {
                form.aggregateLimitAmount.value = '';
                handleChange();
              }
            "
            :disabled="isWorkComp"
            @focusin="handleFocus(true)"
            @focusout="handleFocus(false)" />
        </div>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          :disabled="!form.aggregateLimitApplicable.value || isWorkComp"
          v-model="aggregateLimitAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-visible="!form.aggregateLimitAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Occurrence Limit Amount -->
      <div v-if="form.occurrenceLimitAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <div class="tw:flex tw:justify-between">
          <span class="tw:join-item tw:pl-1">Occurrence Limit</span>
          <input
            v-if="form.occurrenceLimitApplicable.visible"
            type="checkbox"
            v-model="form.occurrenceLimitApplicable.value"
            class="tw:toggle tw:toggle-primary tw:toggle-xs"
            @change="
              () => {
                form.occurrenceLimitAmount.value = '';
                handleChange();
              }
            "
            :disabled="isWorkComp"
            @focusin="handleFocus(true)"
            @focusout="handleFocus(false)" />
        </div>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          :disabled="!form.occurrenceLimitApplicable.value || isWorkComp"
          v-model="occurrenceLimitAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-visible="!form.occurrenceLimitAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Retention Amount -->
      <div v-if="form.retentionAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <div class="tw:flex tw:justify-between">
          <span class="tw:join-item tw:pl-1">Retention</span>
          <input
            v-if="form.retentionApplicable.visible"
            type="checkbox"
            v-model="form.retentionApplicable.value"
            class="tw:toggle tw:toggle-primary tw:toggle-xs"
            @change="
              () => {
                form.retentionAmount.value = '';
                handleChange();
              }
            "
            @focusin="handleFocus(true)"
            @focusout="handleFocus(false)" />
        </div>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          :disabled="!form.retentionApplicable.value"
          v-model="retentionAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-visible="!form.retentionAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Attachment Point Amount -->
      <div v-if="form.attachmentPointAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <div class="tw:flex tw:justify-between">
          <span class="tw:join-item tw:pl-1">Attachment Point</span>
          <input
            v-if="form.attachmentApplicable.visible"
            type="checkbox"
            v-model="form.attachmentApplicable.value"
            class="tw:toggle tw:toggle-primary tw:toggle-xs"
            @change="
              () => {
                form.attachmentPointAmount.value = '';
                handleChange();
              }
            "
            :disabled="form.peFlag.value === 'Primary'"
            @focusin="handleFocus(true)"
            @focusout="handleFocus(false)" />
        </div>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          :disabled="!form.attachmentApplicable.value || form.peFlag.value === 'Primary'"
          v-model="attachmentPointAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-visible="!form.attachmentPointAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Quota Share Total (Total Layer Limit) -->
      <div v-if="form.quotaShareTotalAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Total Layer Limit</span>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          v-model="quotaShareTotalAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-visible="!form.quotaShareTotalAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Carrier Name -->
      <div v-if="form.carrierId.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Carrier Name</span>
        <Combobox
          v-if="isCustomPolicy"
          v-model="formCarrier"
          :options="carrierOptions"
          @update:model-value="handleChange"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)"
          input-class="tw:input-sm tw:h-9" />
        <!-- Don't allow free entry for Bound Policies -->
        <AppSelect
          v-else
          v-model="form.carrierId.value"
          :options="carrierOptions"
          class="tw:h-9 tw:w-full"
          placeholder="Select carrier"
          @update:modelValue="handleChange" />
      </div>

      <!-- Premium Amount -->
      <div v-if="form.policyPremiumAmount.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Premium</span>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          v-model="policyPremiumAmount"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)" />
        <label v-show="!form.policyPremiumAmount.valid" class="tw:label tw:pt-0">
          <span class="tw:label-text-alt tw:text-xs tw:text-red-500"> invalid amount </span>
        </label>
      </div>
      <!-- Broker Name -->
      <div v-if="form.brokerName.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Broker Name</span>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          v-model="form.brokerName.value"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)"
          @input="handleChange" />
      </div>
      <!-- Lead Syndicate -->
      <div v-if="form.leadSyndicateId.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Lead Syndicate</span>
        <AppSelect
          v-model="form.leadSyndicateId.value"
          :options="syndicateOptions"
          class="tw:h-9 tw:w-full"
          placeholder="Select syndicate"
          clearable
          @update:modelValue="handleChange" />
      </div>
      <!-- Custom Retention Label -->
      <div v-if="form.customRetentionLabel.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Custom Retention Label</span>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          v-model="form.customRetentionLabel.value"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)"
          @input="handleChange" />
      </div>
      <!-- Custom Carrier Name -->
      <div v-if="form.customCarrierName.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:gap-1 tw:p-2">
        <span class="tw:join-item tw:pl-1">Custom Carrier Name</span>
        <input
          type="text"
          class="tw:input tw:input-sm tw:join-item tw:h-9 tw:rounded-md"
          v-model="form.customCarrierName.value"
          @focusin="handleFocus(true)"
          @focusout="handleFocus(false)"
          @input="handleChange" />
      </div>
      <div class="tw:flex tw:w-full">
        <!-- Side A -->
        <div v-if="form.sideAValue.visible" class="tw:join tw:join-vertical tw:w-1/2 tw:p-2 tw:gap-1">
          <label v-for="option in SideAOptions" :key="option.label" class="tw:label tw:join-item tw:flex tw:justify-between">
            <span>{{ option.label }}</span>
            <input
              type="radio"
              name="sideA"
              class="tw:radio tw:radio-sm tw:border tw:border-solid"
              :value="option.value"
              v-model="form.sideAValue.value"
              @input="handleChange" />
          </label>
        </div>
        <!-- Runoff Flag -->
        <div v-if="form.runoffFlag.visible" class="tw:join tw:w-1/2 tw:items-start tw:gap-1 tw:p-2">
          <toggle id="runoff" label="Runoff" v-model="form.runoffFlag.value" @update:model-value="handleChange" />
        </div>
      </div>
    </div>
    <div v-if="showPublishButton" class="tw:flex tw:justify-end">
      <app-button class="tw:disabled tw:!btn-xs" :disabled="!isPublishButtonEnabled" primary @click="handlePublish">
        Publish
      </app-button>
    </div>
  </div>
</template>
